import React, { useLayoutEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const Homeabout = () => {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const textRef = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top top",
          end: "+=100%",
          pin: true,
          scrub: true,
          anticipatePin: 1,
          // recalculate start/end on every ScrollTrigger.refresh() call
          invalidateOnRefresh: true,
          refreshPriority: -1,
        },
      });

      tl.fromTo(
        textRef.current,
        { yPercent: 100, immediateRender: false },
        { yPercent: -85, ease: 'none' }
      );
    }, containerRef);

    const doRefresh = () => {
      try { ScrollTrigger.refresh(true); } catch (e) {}
    };

    // Staggered refreshes: after paint, after 300ms, after 800ms
    // This covers fast connections, slow image loads, and font swap
    const t1 = setTimeout(doRefresh, 100);
    const t2 = setTimeout(doRefresh, 300);
    const t3 = setTimeout(doRefresh, 800);

    // Also refresh once all resources (images, fonts) are done
    const onLoad = () => {
      doRefresh();
      setTimeout(doRefresh, 200);
    };

    // Debounced resize
    let resizeTimer = null;
    const onResize = () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(doRefresh, 150);
    };

    window.addEventListener('load', onLoad);
    window.addEventListener('resize', onResize);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      if (resizeTimer) clearTimeout(resizeTimer);
      window.removeEventListener('load', onLoad);
      window.removeEventListener('resize', onResize);
      ctx.revert();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative w-screen h-screen bg-white overflow-hidden flex justify-center items-center z-50 text-black"
    >
      {/* Side Labels */}
      <div className="home-body-text absolute left-2 md:left-4 top-1/2 -translate-y-1/2 text-sm md:text-base font-medium z-20">
        Me
      </div>
      <div className="home-body-text absolute right-2 md:right-4 top-1/2 -translate-y-1/2 text-sm md:text-base font-medium z-20">
        Her
      </div>

      <div className="relative flex flex-col items-center justify-center">
        <div
          className="relative z-10 w-[24vw] h-[18vw] min-w-[150px] min-h-[190px] max-w-[190px] max-h-[235px] md:w-[260px] md:h-[350px] cursor-pointer"
          role="button"
          tabIndex={0}
          aria-label="Open About page"
          onClick={() => navigate('/about')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate('/about');
            }
          }}
        >
          <img
            src="https://nufab-prod-images.s3.ap-south-1.amazonaws.com/products/IMG_6633-1775329663184-e58d1eb8f8dcc456.webp"
            alt="Kei Rose Portrait"
            className="w-full h-full object-cover"
          />
        </div>
        <div
          ref={textRef}
          className="absolute top-0 w-full h-full pointer-events-none z-30"
        >
          <h1 className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-normal tracking-tight text-black whitespace-nowrap text-center">
            Our Story
          </h1>

          <div className="home-body-text absolute top-1/2 left-1/2 -translate-x-1/2 mt-[100px] ml-[95px] md:mt-[96px] md:ml-[132px] lg:ml-[120px] w-[66vw] max-w-60 md:w-72 md:max-w-none lg:w-80 pr-8 sm:pr-3 md:pr-0 text-xs sm:text-sm md:text-base leading-relaxed font-medium text-black">
            <p>
              This studio in its rawest form is a shrine to a mother-daughter relationship. When I was able to answer "your mother lets you wear that?" with "actually, she designed it", I knew this feeling of passing on culture without imposing stale boundaries must spread out of just our family.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Homeabout;
